import { defineStore } from 'pinia';
import { useEasypaisaUserInfoQuery } from '~/data/easypaisa';
import type { EasypaisaUserInfo } from '~/types/easypaisa';

export const useEasyPaisaStore = defineStore('easypaisa', () => {
	const userInfo = ref<EasypaisaUserInfo | null>(null);
	const isEasypaisaMiniapp = ref<boolean>(false);
	const authCode = ref<string | null>(null);
	const openId = ref<string | null>(null);
	const intentId = ref<string | null>(null);
	const isProcessingPayment = ref(false);

	const easypaisaLoading = ref(false);
	const easypaisaIsError = ref(false);
	const easypaisaError = ref<Error | null>(null);

	let queryInstance: ReturnType<typeof useEasypaisaUserInfoQuery> | null = null;

	const initializeQuery = () => {
		if (!queryInstance && authCode.value) {
			try {
				queryInstance = useEasypaisaUserInfoQuery(authCode);

				watchEffect(() => {
					easypaisaLoading.value = queryInstance!.isLoading.value;
					easypaisaIsError.value = queryInstance!.isError.value;
					easypaisaError.value = queryInstance!.error.value;
				});

				watch(
					() => queryInstance!.data.value,
					(data) => {
						if (data) {
							userInfo.value = {
								userName: data.userName,
								userCnic: data.userCnic,
								phoneNumber: data.userMsisdn,
							};
						}
					},
					{ immediate: true },
				);
			} catch (error) {
				console.warn(
					'Vue Query not ready yet, query will be initialized when accessed',
				);
			}
		}

		// If query exists but userInfo is empty, check if we can populate from existing data
		if (queryInstance && !userInfo.value && queryInstance.data.value) {
			userInfo.value = {
				userName: queryInstance.data.value.userName,
				userCnic: queryInstance.data.value.userCnic,
				phoneNumber: queryInstance.data.value.userMsisdn,
			};
		}

		return queryInstance;
	};

	watch(
		authCode,
		(code) => {
			if (code && !queryInstance) {
				initializeQuery();
			}
		},
		{ immediate: true },
	);

	const detectMiniapp = () => {
		if (typeof window === 'undefined') {
			isEasypaisaMiniapp.value = false;
			return;
		}
		try {
			const hasMp =
				typeof (globalThis as any).mp !== 'undefined' ||
				typeof (window as any).mp !== 'undefined';
			isEasypaisaMiniapp.value = !!hasMp;
		} catch {
			isEasypaisaMiniapp.value = false;
		}
	};

	const initiatePayment = (paymentIntentId: string) => {
		if (!isEasypaisaMiniapp.value) return;

		// @ts-expect-error injected by host mini-app
		if (!mp) {
			console.error('Mini-app SDK not injected');
			return;
		}

		intentId.value = paymentIntentId;
		// @ts-expect-error injected by host mini-app
		mp.createPayment(paymentIntentId);
	};

	const setOpenId = (id: string | null) => {
		openId.value = id;
	};

	return {
		userInfo,
		isEasypaisaMiniapp,
		authCode,
		openId,
		intentId,
		isProcessingPayment,
		detectMiniapp,
		initiatePayment,
		setOpenId,
		initializeQuery,
		easypaisaLoading,
		easypaisaIsError,
		easypaisaError,
	};
});
